import { glyph, isCustom, known } from "./emoji";

// Message formatting: Slack's mrkdwn (*bold*, _italic_, ~strike~, <url|text>)
// and the Markdown people type out of habit (**bold**, ~~strike~~,
// [text](url)), plus `code`, ``` blocks, > quotes and lists. parse turns text
// into a tree that RichText renders as React elements: no HTML in the text
// is ever interpreted, and links are only http(s) and mailto.

export type Inline =
  | { t: "text"; v: string }
  | { t: "br" }
  | { t: "code"; v: string }
  | { t: "mention"; id: string }
  | { t: "group"; id: string; label: string }
  | { t: "emoji"; name: string }
  | { t: "link"; href: string; children: Inline[] }
  | { t: "b" | "i" | "s"; children: Inline[] };

export type Block =
  | { t: "p"; children: Inline[] }
  | { t: "pre"; v: string; lang?: string }
  | { t: "quote"; children: Block[] }
  | { t: "ul"; items: Inline[][] }
  | { t: "ol"; start: number; items: Inline[][] };

const url = String.raw`(?:https?:\/\/|mailto:)`;
const inlineRE = new RegExp(
  [
    "`([^`\\n]+)`", // 1 code
    "<@([0-9a-f-]{36})>?", // 2 mention; the > is optional so a cut-off snippet still renders
    `<(${url}[^\\s|>]+)(?:\\|([^>\\n]+))?>`, // 3 url, 4 text: Slack's link
    `\\[([^\\]\\n]+)\\]\\((${url}[^\\s)]+)\\)`, // 5 text, 6 url: Markdown's link
    `(https?:\\/\\/[^\\s<>"]+)`, // 7 a bare link
    "\\*\\*(?=\\S)([^\\n]+?)(?<=\\S)\\*\\*", // 8 **bold**
    "(?<![\\w*])\\*(?=\\S)([^*\\n]*?\\S)\\*(?![\\w*])", // 9 *bold*
    // An _ after a backslash opens nothing, so ¯\_(ツ)_/¯ stays as it is.
    "(?<![\\p{L}\\p{N}_\\\\])_(?=\\S)([^_\\n]*?\\S)_(?![\\p{L}\\p{N}_])", // 10 _italic_
    "~~(?=\\S)([^\\n]+?)(?<=\\S)~~", // 11 ~~strike~~
    "(?<![\\w~])~(?=\\S)([^~\\n]*?\\S)~(?![\\w~])", // 12 ~strike~
    "<!subteam\\^([0-9a-f-]{36})(?:\\|@?([a-z0-9._-]+))?>", // 13 group, 14 its handle as written
  ].join("|"),
  "gu",
);

// A bare link ends before trailing punctuation, and before a ) it did not open.
function trimURL(u: string): string {
  let end = u.length;
  for (;;) {
    const ch = u[end - 1];
    if (".,;:!?'\"".includes(ch)) end--;
    else if (ch === ")" && (u.slice(0, end).match(/\(/g)?.length ?? 0) < (u.slice(0, end).match(/\)/g)?.length ?? 0)) end--;
    else return u.slice(0, end);
  }
}

// :name: becomes its emoji when Lantern knows the name, as in Slack: a
// built-in one its glyph, and a custom one an emoji node.
const shortcode = /:([a-z0-9_+'-]{1,100}(?:::skin-tone-[2-6])?):/g;

function text(v: string, out: Inline[]) {
  v = v.replace(shortcode, (m, name: string) => (known(name) && !isCustom(name) ? glyph(name) : m));
  v.split("\n").forEach((line, i) => {
    if (i > 0) out.push({ t: "br" });
    let last = 0;
    for (const m of line.matchAll(shortcode)) {
      if (!isCustom(m[1])) continue;
      if (m.index > last) out.push({ t: "text", v: line.slice(last, m.index) });
      out.push({ t: "emoji", name: m[1] });
      last = m.index + m[0].length;
    }
    if (last < line.length) out.push({ t: "text", v: line.slice(last) });
  });
}

export function parseInline(s: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  inlineRE.lastIndex = 0;
  for (let m = inlineRE.exec(s); m; m = inlineRE.exec(s)) {
    let end = m.index + m[0].length;
    if (m.index > last) text(s.slice(last, m.index), out);
    if (m[1] !== undefined) out.push({ t: "code", v: m[1] });
    else if (m[2] !== undefined) out.push({ t: "mention", id: m[2] });
    else if (m[3] !== undefined) out.push({ t: "link", href: m[3], children: m[4] ? parseInline(m[4]) : [{ t: "text", v: m[3].replace(/^mailto:/, "") }] });
    else if (m[5] !== undefined) out.push({ t: "link", href: m[6], children: parseInline(m[5]) });
    else if (m[7] !== undefined) {
      const u = trimURL(m[7]);
      end = m.index + u.length;
      out.push({ t: "link", href: u, children: [{ t: "text", v: u }] });
    } else if (m[8] !== undefined || m[9] !== undefined) out.push({ t: "b", children: parseInline(m[8] ?? m[9]) });
    else if (m[10] !== undefined) out.push({ t: "i", children: parseInline(m[10]) });
    else if (m[13] !== undefined) out.push({ t: "group", id: m[13], label: m[14] ?? "group" });
    else out.push({ t: "s", children: parseInline(m[11] ?? m[12]) });
    last = end;
    inlineRE.lastIndex = end;
  }
  if (last < s.length) text(s.slice(last), out);
  return out;
}

const fence = /^```/;
const quote = /^>\s?/;
const bullet = /^\s*[-*•]\s+/;
const numbered = /^\s*(\d{1,9})[.)]\s+/;
const startsBlock = (l: string) => fence.test(l) || quote.test(l) || bullet.test(l) || numbered.test(l);

export function parse(s: string): Block[] {
  const lines = s.replace(/\r\n?/g, "\n").split("\n");
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (fence.test(line)) {
      // ```code``` on one line, or a block up to the closing ```; a word
      // right after the opening fence names its language.
      const rest = line.slice(3);
      const close = rest.indexOf("```");
      if (close >= 0) {
        out.push({ t: "pre", v: rest.slice(0, close) });
        if (rest.slice(close + 3).trim()) out.push({ t: "p", children: parseInline(rest.slice(close + 3).trim()) });
        i++;
        continue;
      }
      const named = /^[\w+#.-]*$/.test(rest.trim());
      const body: string[] = named ? [] : [rest];
      i++;
      while (i < lines.length && !lines[i].includes("```")) body.push(lines[i++]);
      if (i < lines.length) {
        const at = lines[i].indexOf("```");
        if (at > 0) body.push(lines[i].slice(0, at));
        // Text after the closing fence starts the next block.
        const after = lines[i].slice(at + 3);
        if (after.trim()) lines[i] = after;
        else i++;
      }
      out.push(named && rest.trim() ? { t: "pre", v: body.join("\n"), lang: rest.trim().toLowerCase() } : { t: "pre", v: body.join("\n") });
      continue;
    }
    if (line.startsWith(">>>")) {
      // Slack: everything after >>> is quoted.
      out.push({ t: "quote", children: parse([line.slice(3).replace(/^\s/, ""), ...lines.slice(i + 1)].join("\n")) });
      break;
    }
    if (quote.test(line)) {
      const body: string[] = [];
      while (i < lines.length && quote.test(lines[i])) body.push(lines[i++].replace(quote, ""));
      out.push({ t: "quote", children: parse(body.join("\n")) });
      continue;
    }
    if (bullet.test(line)) {
      const items: Inline[][] = [];
      while (i < lines.length && bullet.test(lines[i])) items.push(parseInline(lines[i++].replace(bullet, "")));
      out.push({ t: "ul", items });
      continue;
    }
    const n = numbered.exec(line);
    if (n) {
      const items: Inline[][] = [];
      while (i < lines.length && numbered.test(lines[i])) items.push(parseInline(lines[i++].replace(numbered, "")));
      out.push({ t: "ol", start: Number(n[1]), items });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && !startsBlock(lines[i])) para.push(lines[i++]);
    // Blank lines at the edges of a paragraph come from the blocks around it.
    while (para.length && !para[0].trim()) para.shift();
    while (para.length && !para.at(-1)!.trim()) para.pop();
    if (para.length) out.push({ t: "p", children: parseInline(para.join("\n")) });
  }
  return out;
}

export type Format = "bold" | "italic" | "strike" | "code" | "block" | "quote" | "list" | "numbered";

const marks: Record<"bold" | "italic" | "strike" | "code", string> = { bold: "*", italic: "_", strike: "~", code: "`" };

// applyFormat formats the selection [start, end) of text the way the message
// box's buttons do: wraps it in marks, fences it, or prefixes its lines. It
// returns the new text and selection, the formatted words still selected,
// or the caret between the marks when nothing was.
export function applyFormat(text: string, start: number, end: number, f: Format): { text: string; start: number; end: number } {
  const sel = text.slice(start, end);
  if (f === "block") {
    const before = start > 0 && text[start - 1] !== "\n" ? "\n" : "";
    const open = before + "```\n";
    const close = "\n```" + (end < text.length && text[end] !== "\n" ? "\n" : "");
    return { text: text.slice(0, start) + open + sel + close + text.slice(end), start: start + open.length, end: start + open.length + sel.length };
  }
  if (f === "quote" || f === "list" || f === "numbered") {
    const prefix = (i: number) => (f === "quote" ? "> " : f === "list" ? "- " : `${i + 1}. `);
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    // With nothing selected, the whole line the caret is on.
    if (start === end) end = text.indexOf("\n", end) < 0 ? text.length : text.indexOf("\n", end);
    const block = text.slice(lineStart, end);
    const done = block
      .split("\n")
      .map((l, i) => prefix(i) + l)
      .join("\n");
    return { text: text.slice(0, lineStart) + done + text.slice(end), start: start + prefix(0).length, end: lineStart + done.length };
  }
  const m = marks[f];
  // Spaces around the selection stay outside the marks, or they would not read.
  const lead = sel.length - sel.trimStart().length;
  const trail = sel.length - sel.trimEnd().length;
  const core = sel.trim();
  const a = start + lead;
  const next = text.slice(0, a) + m + core + m + text.slice(end - trail);
  return { text: next, start: a + m.length, end: a + m.length + core.length };
}

// insertLink puts a link where the selection [start, end) was, as
// [label](url), or the bare URL when the label is the URL itself; the caret
// goes after it.
export function insertLink(text: string, start: number, end: number, label: string, url: string): { text: string; start: number; end: number } {
  const md = !label.trim() || label.trim() === url ? url : `[${label.trim().replace(/[[\]]/g, "")}](${url})`;
  const at = start + md.length;
  return { text: text.slice(0, start) + md + text.slice(end), start: at, end: at };
}
